import { getLocationStateByCode, getLocationStateBySlug } from '../config/locationStates';

export const PRICING_LEGACY_PATH_PATTERN = /^\/pricing\/([^/]+)$/i;
export const PRICING_CANONICAL_PATH_PATTERN = /^\/pricing\/([^/]+)\/([^/]+)\/([^/]+)$/i;
export const LOCATION_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const LOCATION_PREFIX_TOKEN_PATTERN = /(?:^|-)((?:location|draft-)[A-Za-z0-9_-]*)$/i;

export const normalizePathname = (pathname = '/') => {
  if (!pathname) return '/';
  const withoutQueryOrHash = String(pathname).split('?')[0]?.split('#')[0] || '/';
  if (withoutQueryOrHash === '/') return '/';
  return withoutQueryOrHash.replace(/\/+$/, '') || '/';
};

const safeDecodeURIComponent = (value = '') => {
  try {
    return decodeURIComponent(value);
  } catch (_error) {
    return String(value || '');
  }
};

export const sanitizeLocationId = (value = '') => {
  if (!value) return '';
  const decoded = safeDecodeURIComponent(String(value || ''));
  return decoded.split(/[&?]/)[0]?.trim() || '';
};

export const slugifySegment = (value = '') => {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const stripLeadingStreetNumber = (street = '') => {
  const normalizedStreet = String(street || '').trim();
  if (!normalizedStreet) return '';
  return normalizedStreet.replace(/^\d+[a-zA-Z]?(?:-\d+)?\s+/u, '').trim();
};

export const parseAddressParts = (address = '') => {
  const parts = String(address || '')
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean);

  const street = parts[0] || '';
  const city = parts[1] || '';
  const stateZip = parts[2] || '';
  const [stateCode = ''] = stateZip.split(/\s+/).filter(Boolean);

  return {
    street,
    city,
    stateCode: String(stateCode || '').toUpperCase(),
  };
};

export const resolveStateCode = (location = null) => {
  if (!location || typeof location !== 'object') return '';

  const explicitStateRaw = String(location.state || '').trim();
  if (explicitStateRaw) {
    const byCode = getLocationStateByCode(explicitStateRaw);
    if (byCode?.code) return byCode.code.toUpperCase();

    const bySlug = getLocationStateBySlug(explicitStateRaw);
    if (bySlug?.code) return bySlug.code.toUpperCase();

    if (/^[a-z]{2}$/i.test(explicitStateRaw)) {
      return explicitStateRaw.toUpperCase();
    }
  }

  const parsedAddress = parseAddressParts(location.address);
  if (parsedAddress.stateCode) {
    const byCode = getLocationStateByCode(parsedAddress.stateCode);
    if (byCode?.code) return byCode.code.toUpperCase();
    if (/^[A-Z]{2}$/.test(parsedAddress.stateCode)) return parsedAddress.stateCode;
  }

  return '';
};

export const getCanonicalPricingSegments = (location = null) => {
  if (!location || typeof location !== 'object') return null;

  const locationId = sanitizeLocationId(location.id || location.locationId || '');
  if (!LOCATION_ID_PATTERN.test(locationId)) return null;

  const addressParts = parseAddressParts(location.address);
  const stateCode = resolveStateCode(location);
  if (!stateCode || !/^[A-Z]{2}$/.test(stateCode)) return null;

  const citySource = addressParts.city || location.city || location.title || locationId;
  const streetSource = addressParts.street || location.title || locationId;
  const streetCore = stripLeadingStreetNumber(streetSource) || streetSource;

  const citySlug = slugifySegment(citySource);
  const streetSlug = slugifySegment(streetCore);
  if (!citySlug || !streetSlug) return null;

  return {
    stateCode: stateCode.toLowerCase(),
    citySlug,
    streetSlug,
    locationId,
  };
};

export const buildCanonicalPricingPath = (location = null) => {
  const segments = getCanonicalPricingSegments(location);
  if (!segments) return null;
  return `/pricing/${segments.stateCode}/${segments.citySlug}/${segments.streetSlug}-${segments.locationId}`;
};

export const extractLocationIdFromStreetToken = (streetLocationToken = '') => {
  const token = sanitizeLocationId(streetLocationToken);
  if (!token) return '';

  const locationPrefixMatch = token.match(LOCATION_PREFIX_TOKEN_PATTERN);
  if (locationPrefixMatch?.[1]) {
    const candidate = sanitizeLocationId(locationPrefixMatch[1]);
    return LOCATION_ID_PATTERN.test(candidate) ? candidate : '';
  }

  const delimiterIndex = token.lastIndexOf('-');
  if (delimiterIndex === -1 || delimiterIndex >= token.length - 1) {
    return '';
  }

  const candidate = sanitizeLocationId(token.slice(delimiterIndex + 1));
  return LOCATION_ID_PATTERN.test(candidate) ? candidate : '';
};

export const parsePricingPath = (pathname = '/') => {
  const normalizedPath = normalizePathname(pathname);

  const canonicalMatch = normalizedPath.match(PRICING_CANONICAL_PATH_PATTERN);
  if (canonicalMatch) {
    const [, rawStateCode = '', rawCitySlug = '', rawStreetLocationToken = ''] = canonicalMatch;
    const locationId = extractLocationIdFromStreetToken(rawStreetLocationToken);
    if (locationId) {
      return {
        isPricingRoute: true,
        routeType: 'canonical',
        pathname: normalizedPath,
        locationId,
        stateCode: slugifySegment(safeDecodeURIComponent(rawStateCode)),
        citySlug: slugifySegment(safeDecodeURIComponent(rawCitySlug)),
        streetLocationToken: safeDecodeURIComponent(rawStreetLocationToken),
      };
    }
  }

  const legacyMatch = normalizedPath.match(PRICING_LEGACY_PATH_PATTERN);
  if (legacyMatch) {
    const locationId = sanitizeLocationId(legacyMatch[1] || '');
    if (LOCATION_ID_PATTERN.test(locationId)) {
      return {
        isPricingRoute: true,
        routeType: 'legacy',
        pathname: normalizedPath,
        locationId,
        stateCode: '',
        citySlug: '',
        streetLocationToken: '',
      };
    }
  }

  return {
    isPricingRoute: false,
    routeType: null,
    pathname: normalizedPath,
    locationId: '',
    stateCode: '',
    citySlug: '',
    streetLocationToken: '',
  };
};

export const isCanonicalPricingPath = (pathname = '/', location = null) => {
  const expectedPath = buildCanonicalPricingPath(location);
  if (!expectedPath) return false;
  return normalizePathname(pathname) === expectedPath;
};

export default {
  LOCATION_ID_PATTERN,
  PRICING_LEGACY_PATH_PATTERN,
  PRICING_CANONICAL_PATH_PATTERN,
  normalizePathname,
  sanitizeLocationId,
  slugifySegment,
  stripLeadingStreetNumber,
  parseAddressParts,
  resolveStateCode,
  getCanonicalPricingSegments,
  buildCanonicalPricingPath,
  extractLocationIdFromStreetToken,
  parsePricingPath,
  isCanonicalPricingPath,
};
